import React from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import ScopeBadge from '../components/ScopeBadge';
import StatusChip from '../components/StatusChip';
import useScopeFilters from '../hooks/useScopeFilters';
import { formatDuration, getInitials } from '../utils/format';
import {
    Box, Typography, Paper, TextField, InputAdornment, Avatar,
    Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Alert, Stack,
    Select, MenuItem, FormControl, InputLabel, Pagination, Button, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, IconButton,
} from '@mui/material';
import { SearchRounded, SyncRounded, CloseRounded } from '@mui/icons-material';

const ALL = ''; // sentinel for "no filter" in the Select components below

export default function Employees() {
    const navigate = useNavigate();
    const {
        ready: scopeReady, units, teams,
        unitId, setUnitId, teamId, setTeamId, showUnitFilter, showTeamFilter,
    } = useScopeFilters('settings.users');
    const [search, setSearch]     = React.useState('');
    const [versions, setVersions] = React.useState([]);
    const [version, setVersion]   = React.useState(ALL);
    const [page, setPage]         = React.useState(1);
    const [employees, setEmployees] = React.useState([]);
    const [pagination, setPagination] = React.useState({ total: 0, pages: 1 });
    const [loading, setLoading]   = React.useState(true);
    const [error, setError]       = React.useState('');
    const [refreshKey, setRefreshKey] = React.useState(0);

    // HRMS backfill sync — only ever for the currently selected single Unit,
    // never "All Units" (whole company). See handleSync below.
    const [syncOpen, setSyncOpen]         = React.useState(false);
    const [syncing, setSyncing]           = React.useState(false);
    const [syncError, setSyncError]       = React.useState('');
    const [syncProgress, setSyncProgress] = React.useState({ processed: 0, total: 0, page: 0, pages: 1 });

    // The App Version option list loads once
    React.useEffect(() => {
        dashboardAPI.getEmployeeVersions()
            .then((res) => { if (res.data.error !== '1') setVersions(res.data.versions || []); })
            .catch(() => { /* non-fatal */ });
    }, []);

    // Any filter change starts back at page 1
    React.useEffect(() => { setPage(1); }, [search, unitId, teamId, version]);

    // Employees — search/unit/team/version/page are all applied server-side.
    // The API hard-caps this at 10 rows per page regardless of what's asked.
    React.useEffect(() => {
        if (!scopeReady) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        const handle = setTimeout(() => {
            dashboardAPI.getEmployees({
                search: search || undefined, unit_id: unitId || undefined,
                team_id: teamId || undefined, version: version || undefined, page,
            })
                .then((res) => {
                    if (cancelled) return;
                    if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load employees');
                    setEmployees(res.data.employees || []);
                    setPagination(res.data.pagination || { total: 0, pages: 1 });
                })
                .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load employees'); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 300);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [scopeReady, search, unitId, teamId, version, page, refreshKey]);

    // Drives the per-page sync loop against POST /api/web/employees/sync,
    // updating progress after every page (10 employees) until HRMS's own
    // pagination says there's nothing left. Refreshes the list on success
    // so newly-synced/updated records show up without a manual reload.
    const handleSync = async () => {
        if (!unitId) return; // defensive — button is disabled/hidden otherwise
        setSyncOpen(true);
        setSyncing(true);
        setSyncError('');
        setSyncProgress({ processed: 0, total: 0, page: 0, pages: 1 });

        let page = 1;
        let processed = 0;
        try {
            for (;;) {
                const res = await dashboardAPI.syncEmployees({ unit_id: unitId, page });
                if (res.data.error === '1') {
                    throw new Error(res.data.errors?.[0]?.Key || 'Sync failed');
                }
                processed += res.data.processed || 0;
                const pg = res.data.pagination || { page, pages: 1, total: processed };
                setSyncProgress({ processed, total: pg.total, page: pg.page || page, pages: Math.max(1, pg.pages || 1) });
                if (!pg.pages || pg.page >= pg.pages) break;
                page += 1;
            }
            setRefreshKey((k) => k + 1);
        } catch (err) {
            setSyncError(err.message || 'Sync failed. Please try again.');
        } finally {
            setSyncing(false);
        }
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h2" sx={{ mb: 0.5 }}>Employees</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1">
                            {loading ? 'Loading…' : `${pagination.total} ${pagination.total === 1 ? 'employee' : 'employees'}`}
                        </Typography>
                        <ScopeBadge featureKey="settings.users" />
                    </Stack>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <TextField
                        size="small"
                        placeholder="Search name, email, or ID…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: { xs: '100%', sm: 280 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRounded sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title={unitId ? '' : 'Select a single Unit first — sync is not available for the whole company at once'}>
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={<SyncRounded />}
                                disabled={!unitId}
                                onClick={handleSync}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                Sync from HRMS
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Filters */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                {showUnitFilter && (
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Unit</InputLabel>
                        <Select label="Unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                            <MenuItem value={ALL}>All Units</MenuItem>
                            {units.map((u) => (
                                <MenuItem key={u.unit_id} value={u.unit_id}>{u.unit_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {showTeamFilter && (
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Team</InputLabel>
                        <Select label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                            <MenuItem value={ALL}>All Teams</MenuItem>
                            {teams.map((t) => (
                                <MenuItem key={t.team_id} value={t.team_id}>{t.team_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>App Version</InputLabel>
                    <Select label="App Version" value={version} onChange={(e) => setVersion(e.target.value)}>
                        <MenuItem value={ALL}>All Versions</MenuItem>
                        {versions.map((v) => (
                            <MenuItem key={v} value={v}>{v}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Employee</TableCell>
                                <TableCell>Shift</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Worked Today</TableCell>
                                <TableCell>App Version</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={5}>
                                            <Skeleton variant="rectangular" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : employees.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No employees found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                employees.map((emp) => (
                                    <TableRow
                                        key={emp.user_id}
                                        hover
                                        onClick={() => navigate(`/employees/${emp.user_id}`)}
                                        sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                                    >
                                        <TableCell>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Avatar sx={{
                                                    width: 34, height: 34, fontSize: '0.75rem', fontWeight: 700,
                                                    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                                                }}>
                                                    {getInitials(emp.name)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>{emp.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {emp.email} · {emp.employee_id}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {emp.shift ? (
                                                <Typography variant="body2">
                                                    {emp.shift.shift_title || emp.shift.shift_type}
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {emp.shift.start_time}–{emp.shift.end_time}
                                                    </Typography>
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell><StatusChip status={emp.today_status} /></TableCell>
                                        <TableCell align="right">{formatDuration(emp.total_usage)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{emp.version || '--'}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
                {pagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <Pagination
                            count={pagination.pages}
                            page={page}
                            onChange={(_, p) => setPage(p)}
                            color="primary"
                            shape="rounded"
                        />
                    </Box>
                )}
            </Paper>

            <Dialog open={syncOpen} onClose={() => !syncing && setSyncOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Syncing from HRMS
                    {!syncing && (
                        <IconButton size="small" onClick={() => setSyncOpen(false)}>
                            <CloseRounded fontSize="small" />
                        </IconButton>
                    )}
                </DialogTitle>
                <DialogContent>
                    {syncError ? (
                        <Alert severity="error">{syncError}</Alert>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {syncProgress.total > 0
                                    ? `${syncProgress.processed} of ${syncProgress.total} employees synced (page ${syncProgress.page} of ${syncProgress.pages})`
                                    : 'Starting…'}
                            </Typography>
                            <LinearProgress
                                variant={syncProgress.total > 0 ? 'determinate' : 'indeterminate'}
                                value={syncProgress.total > 0 ? Math.min(100, (syncProgress.processed / syncProgress.total) * 100) : 0}
                                sx={{ borderRadius: 999, height: 8 }}
                            />
                            {!syncing && !syncError && (
                                <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                                    Done — {syncProgress.processed} employees synced.
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSyncOpen(false)} disabled={syncing}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
